import type { SearchQueryParameters } from '~/types/query-parameters'
import type { SearchDates, SearchGuests, SearchLocation, SearchRentalType } from '~/types/search'
import searchFormMessages from '~~/i18n/locales/common/search/form.json'

export default function useSearchForm() {
  const searchFormData = useSearchFormData()
  const { appliedRentalType, navigateToRvSearch, navigateToCampgroundsSearch, searchData } = useSearch()

  const { t } = useI18n({ useScope: 'global', messages: searchFormMessages })

  // Location parameter
  const nearbyText = computed(() => t('searchForm.location.nearby'))

  function updateSearchFormLocation(location?: SearchLocation) {
    searchFormData.value.location = location
  }

  function clearSearchFormLocation() {
    // Map bounds are not in the form draft. Empty placeId signals submit to rewrite the
    // location group so NeLat/NeLng/SwLat/SwLng drop out of the URL.
    if (searchData.value.bounds) {
      searchFormData.value.location = { placeId: '', text: '' }
      return
    }

    searchFormData.value.location = undefined
  }

  // Dates parameter
  const { formatAsShortDate, formatAsShortDateRange } = useDates()

  const computedDraftDatesLabel = computed(() => {
    if (!searchFormData.value.dates?.start) {
      return undefined
    }

    if (searchFormData.value.dates?.end) {
      return formatAsShortDateRange(searchFormData.value.dates.start, searchFormData.value.dates.end)
    }

    return `${formatAsShortDate(searchFormData.value.dates.start)} -`
  })

  function updateSearchFormDates(dates?: SearchDates) {
    searchFormData.value.dates = dates
  }

  function clearSearchFormDates() {
    searchFormData.value.dates = undefined
  }

  // Guests parameter
  const computedDraftGuestsLabel = computed(() => {
    if (!searchFormData.value.guests?.adults && !searchFormData.value.guests?.children && !searchFormData.value.guests?.petFriendly) {
      return undefined
    }

    return `${t('searchForm.guests.count', { n: (searchFormData.value.guests?.adults ?? 0) + (searchFormData.value.guests?.children ?? 0) })} ${searchFormData.value.guests?.petFriendly ? t('searchForm.guests.petsSuffix') : ''}`
  })

  function updateSearchFormGuests(guests?: SearchGuests) {
    searchFormData.value.guests = guests
      ? {
          adults: guests.adults || (guests.petFriendly ? 1 : 0),
          children: guests.children ?? 0,
          petFriendly: guests.petFriendly ?? false,
        }
      : undefined
  }

  function clearSearchFormGuests() {
    searchFormData.value.guests = undefined
  }

  // Rental type parameter
  const computedDraftRentalTypeLabel = computed(() => {
    if (!searchFormData.value.rentalType) {
      return undefined
    }

    return t(`searchForm.rentalType.${searchFormData.value.rentalType}`)
  })

  function updateSearchFormRentalType(rentalType?: SearchRentalType) {
    searchFormData.value.rentalType = rentalType
  }

  function clearSearchFormRentalType() {
    searchFormData.value.rentalType = undefined
  }

  function clearSearchForm() {
    clearSearchFormLocation()
    clearSearchFormDates()
    clearSearchFormGuests()
    clearSearchFormRentalType()
  }

  /**
   * All dates up to (but not including) today should be disabled.
   */
  const { userTimezone } = useDates()
  const disabledDates = computed(() => {
    const today = datetime.utc().tz(userTimezone.value)

    // If today is the first of the month, return an empty array.
    if (today.date() === 1) {
      return []
    }

    return [
      {
        start: today.startOf('month').toDate(),
        end: today.subtract(1, 'day').toDate(),
      },
    ]
  })

  const {
    isRouteRvRentalsCampgroundPage,
    isRouteRvRentalsParkPage,
    isRouteRvRentalsEventPage,
    isRouteRvRentalsDestinationPage,
    isRouteRvSearchPage,
  } = useBaseName()

  const isPoiSearchPage = computed(() =>
    isRouteRvRentalsCampgroundPage.value
    || isRouteRvRentalsParkPage.value
    || isRouteRvRentalsEventPage.value
    || isRouteRvRentalsDestinationPage.value,
  )

  /**
   * The form is a draft of the applied search, so it restarts from whatever the URL
   * currently describes. `submitSearchForm` diffs the draft against that same source,
   * so a field the draft never received would otherwise look like the user cleared it.
   *
   * Event, park, and campground pages pre-populate a location without putting PlaceId
   * in the URL. Keep that draft when applied search has no placeId so a later
   * `useSearchForm()` call (e.g. LazySearchBar) does not wipe it.
   */
  function resetSearchFormToAppliedSearch() {
    const placeId = searchData.value.location?.placeId

    if (placeId) {
      updateSearchFormLocation({ placeId, text: searchData.value.location?.name ?? '' })
    }
    else if (!isPoiSearchPage.value) {
      updateSearchFormLocation()
    }

    updateSearchFormDates(searchData.value.dates)
    updateSearchFormGuests(searchData.value.guests)
    updateSearchFormRentalType(appliedRentalType.value)
  }

  watch(
    [
      () => searchData.value.location?.placeId,
      () => searchData.value.dates,
      () => searchData.value.guests,
      appliedRentalType,
      isPoiSearchPage,
    ],
    resetSearchFormToAppliedSearch,
    { immediate: true },
  )

  async function submitCampgroundsSearchForm() {
    const { location } = searchFormData.value
    await navigateToCampgroundsSearch({ PlaceId: location?.placeId })
  }

  function resolveSubmitPlaceId(searchFormPlaceId?: Nullable<string>, searchDataPlaceId?: Nullable<string>) {
    if (searchFormPlaceId !== undefined) {
      return searchFormPlaceId || undefined
    }

    return searchDataPlaceId || undefined
  }

  async function submitSearchForm() {
    const { location, dates, guests, rentalType } = searchFormData.value
    const applied = searchData.value

    if (!isRouteRvSearchPage.value) {
      await navigateToRvSearch({
        PlaceId: resolveSubmitPlaceId(location?.placeId, applied.location?.placeId),
        StartDate: dates?.start,
        EndDate: dates?.end,
        Adults: guests?.adults,
        Children: guests?.children,
        PetFriendly: guests?.petFriendly,
        Delivery: rentalType === 'delivery',
        Drivable: rentalType === 'drivable',
        Towable: rentalType === 'towable',
      })

      return
    }

    /**
     * Only the fields the form actually changed are sent. Passing a key is what tells
     * `navigateToRvSearch` to rewrite that group, so a field the user left alone has
     * to stay absent to preserve values the form cannot express, such as map bounds.
     * A field the user cleared is sent as undefined, which is what removes it.
     */
    const params: SearchQueryParameters = {}

    if (location?.placeId !== applied.location?.placeId) {
      params.PlaceId = location?.placeId || undefined
    }

    if (
      (dates?.start !== applied.dates?.start || dates?.end !== applied.dates?.end)
      && Boolean(dates?.start) === Boolean(dates?.end)
    ) {
      params.StartDate = dates?.start
      params.EndDate = dates?.end
    }

    if (guests?.adults !== applied.guests?.adults
      || guests?.children !== applied.guests?.children
      || guests?.petFriendly !== applied.guests?.petFriendly) {
      params.Adults = guests?.adults
      params.Children = guests?.children
      params.PetFriendly = guests?.petFriendly
    }

    if (rentalType !== appliedRentalType.value) {
      params.Delivery = rentalType === 'delivery'
      params.Drivable = rentalType === 'drivable'
      params.Towable = rentalType === 'towable'
    }

    await navigateToRvSearch(params)
  }

  return {
    computedDraftDatesLabel,
    computedDraftGuestsLabel,
    computedDraftRentalTypeLabel,

    searchFormData,
    disabledDates,
    nearbyText,

    updateSearchFormLocation,
    clearSearchFormLocation,
    updateSearchFormDates,
    clearSearchFormDates,
    updateSearchFormGuests,
    clearSearchFormGuests,
    updateSearchFormRentalType,
    clearSearchFormRentalType,
    clearSearchForm,
    submitCampgroundsSearchForm,
    submitSearchForm,
  }
}
